import React, { isValidElement } from 'react';
import type { ReactNode } from 'react';

import Tooltip from '@mui/material/Tooltip';
import type { TooltipProps } from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';
import classNames from 'classnames';
import { isMobile } from 'react-device-detect';
import { withStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';

const styles = (theme: Theme) => ({
  infoTip: {
    ...theme.typography.paragraphRegular,
    padding: '16px 16px 16px 16px',
    background: theme.palette.text.primary,
    color: theme.palette.background.paper,
    borderRadius: 12,
    borderColor: theme.palette.border.main,
  },
  infoTipInverted: {
    ...theme.typography.paragraphRegular,
    padding: '16px 16px 16px 16px',
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: 12,
    borderColor: theme.palette.border.main,
  },
  infoArrow: {
    color: theme.palette.text.primary,
  },
  infoArrowInverted: {
    color: theme.palette.background.paper,
  },
});

export type QTipProps = Omit<TooltipProps, 'children' | 'classes' | 'ref' | 'title'> & {
  children: ReactNode;
  /** Text content; blank text shows no tooltip. With `wrapOnly`, defaults to the wrapped text. */
  title?: string | null;
  /** Element content. Takes precedence over `title`. */
  html?: ReactNode;
  /** Mobile browsers show the tooltip only with this set. */
  enableOnMobile?: boolean;
  /** Shows the tooltip only if the wrapped text overflows, as measured on mount and when the title changes. */
  wrapOnly?: boolean;
  /** Class for the wrapper span, which renders only when no tooltip shows. */
  className?: string;
  /** Fills the tooltip with the theme's text color, with paper-colored text. */
  solidTheme?: boolean;
  /** With `solidTheme`, `inverted` swaps those colors and adds a shadow. */
  variant?: 'standard' | 'inverted';
  tooltipClass?: string;
  arrowClass?: string;
  popperProps?: TooltipProps['PopperProps'];
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
};

type QTipState = {
  overflow: boolean;
};

type TextElement = HTMLElement & { value?: unknown };

const isTextElement = (element: Element): element is TextElement =>
  element instanceof HTMLElement && Boolean(element.innerText || ('value' in element && element.value));

// The first innermost element with text or a value.
const findTextElement = (element: Element | null): TextElement | undefined => {
  if (!element) return undefined;
  for (const child of Array.from(element.children)) {
    const textElement = findTextElement(child);
    if (textElement) return textElement;
  }
  return isTextElement(element) ? element : undefined;
};

const isElementOverflow = (element: HTMLElement | undefined) =>
  Boolean(element && (element.clientWidth < element.scrollWidth || element.clientHeight < element.scrollHeight));

class QTip extends React.Component<QTipProps, QTipState> {
  state: QTipState = { overflow: false };

  // The wrapped text, measured with the overflow. Used when `title` is missing.
  title: string | undefined = undefined;

  defaultId = uuidv4();

  componentDidMount() {
    if (this.props.wrapOnly) {
      this.checkOverflow();
    }
  }

  // Measures before the new title renders, so the new content mounts once, in its final wrapper.
  UNSAFE_componentWillReceiveProps(nextProps: QTipProps) {
    if (nextProps.wrapOnly && nextProps.title !== this.props.title) {
      this.setState({ overflow: false });
      this.checkOverflow();
    }
  }

  checkOverflow = () => {
    const textElement = findTextElement(document.getElementById(`QtipId:${this.defaultId}`));
    this.title = textElement?.innerText || String(textElement?.value);
    this.setState({ overflow: isElementOverflow(textElement) });
  };

  render() {
    const {
      children,
      title = this.title,
      html,
      classes: _classes,
      className,
      popperProps,
      arrowClass,
      tooltipClass,
      enableOnMobile,
      placement,
      solidTheme,
      wrapOnly,
      variant,
      ...other
    } = this.props;
    const classes = withStyles.getClasses(this.props);
    const id = this.defaultId;

    if (
      (!isMobile || enableOnMobile) &&
      (html || (typeof title === 'string' && title.trim().length > 0)) &&
      (!wrapOnly || this.state.overflow)
    ) {
      return (
        <Tooltip
          enterDelay={500}
          id={`tooltip:${id}`}
          leaveDelay={200}
          placement={placement || 'top'}
          title={html || title || ''}
          classes={{
            tooltip: solidTheme
              ? classNames(variant === 'inverted' ? classes.infoTipInverted : classes.infoTip, tooltipClass)
              : tooltipClass,
            arrow: solidTheme
              ? classNames(variant === 'inverted' ? classes.infoArrowInverted : classes.infoArrow, arrowClass)
              : arrowClass,
          }}
          PopperProps={popperProps}
          slotProps={variant === 'inverted' ? { tooltip: { sx: { boxShadow: 3 } } } : undefined}
          {...other}
        >
          {/* Tooltip already wraps anything but a single element in a span; doing it here satisfies its type. */}
          {isValidElement(children) ? children : <span>{children}</span>}
        </Tooltip>
      );
    }
    return (
      <span id={`QtipId:${id}`} className={className}>
        {children}
      </span>
    );
  }
}

export default withStyles(QTip, styles);
